import type { SortableItemModel } from "@octopusdeploy/design-system-components";
import { Button, Callout, SortableList } from "@octopusdeploy/design-system-components";
import { links } from "@octopusdeploy/portal-routes";
import classNames from "classnames";
import type { ReactNode } from "react";
import type React from "react";
import { useState } from "react";
import { v4 } from "uuid";
import { LifecycleContextualHelp } from "~/components/ContextualHelp/ContextualHelpSnippets";
import { CustomDialogContent } from "~/components/DialogLayout/Custom";
import CustomSaveDialogLayout, { CustomSaveDialogTitleBar } from "~/components/DialogLayout/Custom/CustomSaveDialogLayout";
import InternalLink from "../Navigation/InternalLink";
import styles from "./styles.module.less";
interface CreateEnvironmentsDialogLayoutProps {
    spaceId: string;
    title?: ReactNode;
    skipButtonTitle?: string;
    backButtonTitle?: string;
    saveButtonTitle?: string;
    hideBackButton?: boolean;
    onSaveClick: (environments: Array<string>) => Promise<boolean>;
    onSkipClick: () => boolean;
}
const initialEnvironmentNames = ["Development", "Staging", "Production"];
export default function CreateEnvironmentsDialogLayout(props: CreateEnvironmentsDialogLayoutProps) {
    const initialEnvironments = initialEnvironmentNames.map((envName) => ({ Name: envName, Id: v4() }));
    const [selectedEnvironments, setSelectedEnvironments] = useState<Array<SortableItemModel>>(initialEnvironments);
    const [environmentsAreValid, setEnvironmentsAreValid] = useState<boolean | undefined>(undefined);
    const onEnvironmentAdded = () => {
        const newEnv: SortableItemModel = { Name: "", Id: v4() };
        setSelectedEnvironments([...selectedEnvironments, newEnv]);
    };
    const onSaveClicked = async () => {
        const saveResult = await props.onSaveClick(selectedEnvironments.map((x) => x.Name));
        setEnvironmentsAreValid(saveResult);
        return saveResult;
    };
    return (<CustomSaveDialogLayout frame={FullscreenFrame} onSaveClick={onSaveClicked} close={props.onSkipClick} open={true} renderTitle={() => <CreateEnvironmentsDialogTitle title={props.title}/>} renderActions={(renderProps) => (<div className={classNames(styles.buttonContainer, styles.buttonsContainerFullScreen)}>
                    <Button importance="quiet" label={props.skipButtonTitle ?? "Skip"} onClick={() => {
                props.onSkipClick?.();
            }}/>
                    {!props.hideBackButton && <Button importance="primary" label="Back" disabled={true} onClick={() => { }}/>}
                    <Button importance="primary" label={props.saveButtonTitle ?? "Save"} onClick={renderProps.onSaveClick}/>
                </div>)} renderContent={() => (<CustomDialogContent className={styles.customDialogContentFullScreen}>
                    {environmentsAreValid === false && (<Callout type="danger" title="Environments couldn’t be saved">
                            <ol className={styles.savingError}>
                                <li>One or more environment names do not meet the criteria. Please review these guidelines and correct the names before trying again:</li>
                                <li>Ensure each environment name is unique.</li>
                                <li>Each name should be 50 characters or less.</li>
                                <li>Use only letters (A-Z), numbers (0-9), periods (.), commas (,), dashes (-), underscores (_), and hashes (#).</li>
                            </ol>
                        </Callout>)}

                    <div className={styles.lifecycle}>
                        Environments will appear in your <strong>Lifecycle</strong> <LifecycleContextualHelp /> in this order:
                    </div>
                    {(!selectedEnvironments || !selectedEnvironments.length) && (<Callout type="warning" title="No environments added">
                            Add environments now or later in{" "}
                            <InternalLink to={links.environmentsPage.generateUrl({ spaceId: props.spaceId })} openInSelf={false}>
                                Environments
                            </InternalLink>
                            .
                        </Callout>)}
                    <div className={styles.environmentsContainerFullScreen}>
                        <SortableList items={selectedEnvironments} label="Environment" onItemsUpdated={setSelectedEnvironments} isItemEditable={true}/>
                    </div>
                    <div className={classNames(styles.addEnvironmentContainer, styles.addEnvironmentContainerFullScreen)}>
                        <Button importance="tertiary" label="Add Environment" onClick={onEnvironmentAdded}/>
                    </div>
                </CustomDialogContent>)}></CustomSaveDialogLayout>);
}
const FullscreenFrame: React.FC<{
    children?: React.ReactNode;
}> = ({ children }) => <div className={styles.noFrameFlex}>{children}</div>;
FullscreenFrame.displayName = "FullscreenFrame"
interface CreateEnvironmentsDialogTitleProps {
    title: ReactNode;
}
function CreateEnvironmentsDialogTitle(props: CreateEnvironmentsDialogTitleProps) {
    return (<div>
            <div>{props.title}</div>
            <CustomSaveDialogTitleBar title="You need at least one environment to deploy to" className={styles.dialogTitleFullScreen}/>
        </div>);
}
